import React, { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Route, Switch, Redirect, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import SnackbarProvider from 'react-simple-snackbar'
import Home from './components/Home/Home';
import RouteMetadata from './seo/RouteMetadata'
import Header from './components/Header/Header';
import { refreshSession } from './actions/auth'
import { selectAuthData } from './store/selectors/authSelectors'
import { useHomeDocument } from './utils/homeTheme'

const Invoice = lazy(() => import('./components/Invoice/Invoice'))
const Invoices = lazy(() => import('./components/Invoices-DataGrid/Invoices'))
const InvoiceDetails = lazy(() => import('./components/InvoiceDetails/InvoiceDetails'))
const White = lazy(() => import('./components/White/White'))
const Whites = lazy(() => import('./components/Whites-DataGrid/Whites'))
const WhiteDetails = lazy(() => import('./components/WhiteDetails/WhiteDetails'))
const ClientList = lazy(() => import('./components/Clients/ClientList'))
const LockupList = lazy(() => import('./components/Lockup/LockupList'))
const ProductList = lazy(() => import('./components/Products-DataGrid/ProductList'))
const InventoryList = lazy(() => import('./components/Inventory/InventoryList'))
const Routes = lazy(() => import('./components/Routes/Routes'))
const OrderSheet = lazy(() => import('./components/OrderSheet/OrderSheet'))
const NavBar = lazy(() => import('./components/NavBar/NavBar'))
const Login = lazy(() => import('./components/Login/Login'))
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'))
const Footer = lazy(() => import('./components/Footer/Footer'))
const Settings = lazy(() => import('./components/Settings/Settings'))
const Forgot = lazy(() => import('./components/Password/Forgot'))
const Reset = lazy(() => import('./components/Password/Reset'))
const RevenueDashboardPage = lazy(() => import('./pages/RevenueDashboardPage'))
const RecentlyDeletedPage = lazy(() => import('./pages/RecentlyDeletedPage'))
const ActivityLogPage = lazy(() => import('./pages/ActivityLogPage'))
const WorkCalendarPage = lazy(() => import('./pages/WorkCalendarPage'))
const OutstandingInvoicesPage = lazy(() => import('./pages/OutstandingInvoicesPage'))
const InvoicePaymentPage = lazy(() => import('./pages/InvoicePaymentPage'))
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'))
const QuoteBuilderPage = lazy(() => import('./pages/QuoteBuilderPage'))
const QuotesPage = lazy(() => import('./pages/QuotesPage'))
const RoutePlannerPage = lazy(() => import('./pages/RoutePlannerPage'))
const RouteLoadSheetPage = lazy(() => import('./pages/RouteLoadSheetPage'))
const RouteLoadSheetCollectionPage = lazy(() => import('./pages/RouteLoadSheetCollectionPage'))

const RoutedFooter = () => {
  const location = useLocation()
  if (location.pathname === '/') return null
  return <Footer />
}

function AppShell() {
  const authData = useSelector(selectAuthData)
  const dispatch = useDispatch()
  const location = useLocation()
  const isHome = location.pathname === '/'
  const isPaymentRoute = location.pathname.startsWith('/pay/')
  useHomeDocument(isHome)

  useEffect(() => {
    // Client JSS has mounted; remove the build-time copy of the public icon styles.
    document.getElementById('home-prerender-styles')?.remove()
  }, [])

  useEffect(() => {
    if (!isPaymentRoute) dispatch(refreshSession())
  }, [dispatch, isPaymentRoute])

  return (
    <div className="appShell">
      <RouteMetadata />
      <Suspense fallback={<div role="status" aria-label="Loading" />}>
        {authData && !isHome && !isPaymentRoute && (
          <div className="print-hidden">
            <NavBar />
          </div>
        )}
        {!isPaymentRoute && <div className="print-hidden">
          <Header />
        </div>}
        <div className="appMain">
          <Switch>
            <Route path="/pay/:token/success" exact component={PaymentSuccessPage} />
            <Route path="/pay/:token" exact component={InvoicePaymentPage} />
            <Route path="/" exact component={Home} />
            <Route path="/invoice" exact component={Invoice} />
            <Route path="/edit/invoice/:id" exact component={Invoice} />
            <Route path="/invoice/:id" exact component={InvoiceDetails} />
            <Route path="/white" exact component={White} />
            <Route path="/edit/white/:id" exact component={White} />
            <Route path="/invoices" exact component={Invoices} />
            <Route path="/white/:id" exact component={WhiteDetails} />
            <Route path="/whites" exact component={Whites} />
            <Route path="/login" exact component={Login} />
            <Route path="/settings" exact component={Settings} />
            <Route path="/dashboard" exact component={Dashboard} />
            <Route path="/customers" exact component={ClientList} />
            <Route path="/lockups" exact component={LockupList} />
            <Route path="/inventory" exact component={InventoryList} />
            <Route path="/products" exact component={ProductList} />
            <Route path="/routes" exact component={Routes} />
            <Route path="/routes/print" exact component={RouteLoadSheetCollectionPage} />
            <Route path="/routes/:planId" exact component={RouteLoadSheetPage} />
            <Route path="/routes/print" exact component={RouteLoadSheetCollectionPage} />
            <Route path="/order-sheet" exact component={OrderSheet} />
            <Route path="/forgot" exact component={Forgot} />
            <Route path="/reset/:token" exact component={Reset} />
            <Route path="/analytics" exact component={RevenueDashboardPage} />
            <Route path="/recently-deleted" exact component={RecentlyDeletedPage} />
            <Route path="/activity-log" exact component={ActivityLogPage} />
            <Route path="/work-calendar" exact component={WorkCalendarPage} />
            <Route path="/outstanding-invoices" exact component={OutstandingInvoicesPage} />
            <Route path="/quote-builder" exact component={QuoteBuilderPage} />
            <Route path="/quote-builder/:id" exact component={QuoteBuilderPage} />
            <Route path="/quotes" exact component={QuotesPage} />
            <Route path="/routes-planner" exact component={RoutePlannerPage} />
            <Redirect exact from="/new-invoice" to="/invoice" />
          </Switch>
        </div>
        {!isPaymentRoute && <div className="print-hidden">
          <RoutedFooter />
        </div>}
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SnackbarProvider>
        <AppShell />
      </SnackbarProvider>
    </BrowserRouter>
  )
}

export default App;
